import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Users, BookOpen, Calendar, Eye, UserCheck, Mail, Send, Trash2, CheckCircle2, KeyRound, Link, Copy, Plus, Edit, RefreshCw, ChevronDown, ChevronUp, BarChart3, MessageSquarePlus } from 'lucide-react';
import { AdminAnalytics } from '../components/AdminAnalytics';
import { BulkSermonImport } from '../components/BulkSermonImport';
import { AdminPendingPrayers } from '../components/AdminPendingPrayers';
import { AdminPrayerModerators } from '../components/AdminPrayerModerators';
import AdminAdministratorsPanel from '../components/AdminAdministratorsPanel';
import ConfidentialPrayersPanel from '../components/ConfidentialPrayersPanel';
import TopSearchesPanel from '../components/TopSearchesPanel';
import SendDirectMessageDialog from '../components/SendDirectMessageDialog';

const AdminDashboard = () => {
  const { user, API } = useContext(AuthContext);
  // Scoped view: prayer-only moderators (non-full-admin) get a stripped-down UI.
  const isPrayerOnly = !!(user && user.role !== 'admin' && user.is_prayer_moderator);
  const [allProgress, setAllProgress] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userJournals, setUserJournals] = useState([]);
  const [userCheckins, setUserCheckins] = useState([]);
  const [loading, setLoading] = useState(true);
  // Direct-message dialog state (admin messages any member)
  const [dmOpen, setDmOpen] = useState(false);
  const [dmRecipient, setDmRecipient] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  
  // Private messages state
  const [privateMessages, setPrivateMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  
  // Password reset state
  const [resetEmail, setResetEmail] = useState('');
  const [generatingReset, setGeneratingReset] = useState(false);
  const [resetLinkData, setResetLinkData] = useState(null);

  // Sermon sync state
  const [syncingSermons, setSyncingSermons] = useState(false);
  
  // Sermon management state
  const [allSermons, setAllSermons] = useState([]);
  const [loadingSermons, setLoadingSermons] = useState(false);
  const [showSermonForm, setShowSermonForm] = useState(false);
  const [newSermonDate, setNewSermonDate] = useState('');
  const [newSermonPassage, setNewSermonPassage] = useState('');
  const [addingSermon, setAddingSermon] = useState(false);
  const [expandedSermon, setExpandedSermon] = useState(null);
  const [editingSermon, setEditingSermon] = useState(null);

  useEffect(() => {
    // Prayer-only moderators don't have permission to hit these admin-only
    // endpoints (they'd all 403), and don't need the data anyway.
    if (isPrayerOnly) {
      setLoading(false);
      return;
    }
    fetchAllProgress();
    fetchPrivateMessages();
    fetchAllSermons();
  }, [isPrayerOnly]);

  const fetchAllProgress = async () => {
    try {
      const response = await axios.get(`${API}/admin/progress`);
      setAllProgress(response.data);
    } catch (error) {
      toast.error('Failed to load member progress');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrivateMessages = async () => {
    setLoadingMessages(true);
    try {
      const response = await axios.get(`${API}/admin/messages`);
      setPrivateMessages(response.data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleReply = async (messageId) => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }
    
    setSendingReply(true);
    try {
      await axios.post(`${API}/admin/messages/${messageId}/reply`, {
        reply: replyText.trim()
      });
      toast.success('Reply sent!');
      setReplyText('');
      setReplyingTo(null);
      fetchPrivateMessages();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleMarkRead = async (messageId) => {
    try {
      await axios.post(`${API}/admin/messages/${messageId}/read`);
      fetchPrivateMessages();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      await axios.delete(`${API}/admin/messages/${messageId}`);
      toast.success('Message deleted');
      fetchPrivateMessages();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleGenerateResetLink = async () => {
    if (!resetEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setGeneratingReset(true);
    try {
      const response = await axios.post(`${API}/admin/generate-reset-link`, { email: resetEmail });
      const baseUrl = window.location.origin;
      const resetLink = `${baseUrl}/forgot-password?token=${response.data.reset_token}`;

      setResetLinkData({
        link: resetLink,
        userName: response.data.user_name,
        userEmail: response.data.user_email,
        emailSent: !!response.data.email_sent,
        emailError: response.data.email_error || null,
      });

      if (response.data.email_sent) {
        toast.success(`Reset link emailed to ${response.data.user_name} (${response.data.user_email})`);
      } else {
        toast.success(`Reset link generated for ${response.data.user_name} — copy and share`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate reset link');
    } finally {
      setGeneratingReset(false);
    }
  };

  const handleSyncSermons = async () => {
    setSyncingSermons(true);
    try {
      const response = await axios.post(`${API}/admin/sync-new-sermons`);
      toast.success(response.data.message);
      fetchAllSermons(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync sermons');
    } finally {
      setSyncingSermons(false);
    }
  };

  const fetchAllSermons = async () => {
    setLoadingSermons(true);
    try {
      const response = await axios.get(`${API}/sermons/all`);
      setAllSermons(response.data);
    } catch (error) {
      console.error('Failed to load sermons:', error);
    } finally {
      setLoadingSermons(false);
    }
  };

  const handleAddSermon = async () => {
    if (!newSermonDate || !newSermonPassage.trim()) {
      toast.error('Please enter both date and passage');
      return;
    }
    
    setAddingSermon(true);
    try {
      const response = await axios.post(`${API}/admin/sermons/add`, {
        week_start_date: newSermonDate,
        passage: newSermonPassage.trim()
      });
      toast.success(response.data.message);
      setNewSermonDate('');
      setNewSermonPassage('');
      setShowSermonForm(false);
      fetchAllSermons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add sermon');
    } finally {
      setAddingSermon(false);
    }
  };

  const handleDeleteSermon = async (sermonId, passage) => {
    if (!window.confirm(`Delete sermon "${passage}"? This cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/admin/sermons/${sermonId}`);
      toast.success('Sermon deleted');
      fetchAllSermons();
    } catch (error) {
      toast.error('Failed to delete sermon');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleViewDetails = async (userProgress) => {
    setSelectedUser(userProgress.user);
    setDetailsOpen(true);
    
    try {
      const [journalsRes, checkinsRes] = await Promise.all([
        axios.get(`${API}/admin/user/${userProgress.user.id}/journals`),
        axios.get(`${API}/admin/user/${userProgress.user.id}/checkins`)
      ]);
      
      setUserJournals(journalsRes.data);
      setUserCheckins(checkinsRes.data);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      explore: 'bg-[#2B6B7F] text-white',
      engage: 'bg-[#5FA8C1] text-white',
      extend: 'bg-[#7BA888] text-white'
    };
    return colors[stage] || 'bg-slate-200';
  };

  const getStageLabel = (stage) => {
    const labels = {
      explore: 'Explore - Connect',
      engage: 'Engage - Grow',
      extend: 'Extend - Neighbor'
    };
    return labels[stage] || stage;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getVitalSignLabel = (key) => {
    const labels = {
      pursue_god: 'Pursue God',
      engage_scripture: 'Engage Scripture',
      serve_others: 'Serve Others',
      radiate_jesus: 'Radiate Jesus'
    };
    return labels[key] || key;
  };

  // Calculate summary stats
  const totalMembers = allProgress.length;
  const totalJournals = allProgress.reduce((sum, p) => sum + p.journal_count, 0);
  const totalCheckins = allProgress.reduce((sum, p) => sum + p.checkin_count, 0);
  const activeMembers = allProgress.filter(p => p.journal_count > 0 || p.checkin_count > 0).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
        <Navigation />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-lg text-slate-600">Loading...</div>
        </div>
      </div>
    );
  }

  // Scoped-role early return: prayer moderators see ONLY the prayer approval queue.
  if (isPrayerOnly) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50" data-testid="admin-dashboard-prayer-only">
        <Navigation />
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="mb-8 animate-fade-in">
            <h1 className="text-4xl font-heading font-bold text-[#2B6B7F] mb-2">
              Prayer Moderation
            </h1>
            <p className="text-slate-600">
              Review, approve, and manage prayer requests submitted through the app and the church website.
            </p>
          </div>
          <AdminPendingPrayers />
          <ConfidentialPrayersPanel />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50" data-testid="admin-dashboard">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-8 animate-fade-in">
          <h1 className="text-4xl font-heading font-bold text-[#2B6B7F] mb-2">
            Admin Dashboard
          </h1>
          <p className="text-slate-600">Monitor spiritual growth across your congregation</p>
        </div>

        {/* Top-level tabs: Analytics vs Management */}
        <Tabs defaultValue="analytics" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 max-w-md" data-testid="admin-top-tabs">
            <TabsTrigger value="analytics" data-testid="tab-analytics">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="management" data-testid="tab-management">
              <Users className="w-4 h-4 mr-2" />
              Management
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analytics" className="space-y-6" data-testid="analytics-tab-content">

        {/* Summary Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="card-hover" data-testid="stat-total-members">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Total Members</p>
                  <p className="text-3xl font-bold text-[#2B6B7F]">{totalMembers}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#2B6B7F]/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#2B6B7F]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-active-members">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Active Members</p>
                  <p className="text-3xl font-bold text-[#5FA8C1]">{activeMembers}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#5FA8C1]/10 flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-[#5FA8C1]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-total-journals">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Total Journals</p>
                  <p className="text-3xl font-bold text-[#7BA888]">{totalJournals}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#7BA888]/10 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-[#7BA888]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-total-checkins">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Total Check-Ins</p>
                  <p className="text-3xl font-bold text-[#D4A574]">{totalCheckins}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#D4A574]/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-[#D4A574]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Prayer Moderators panel moved to Management tab */}

        {/* Member Progress Table */}
        <Card data-testid="members-table">
          <CardHeader>
            <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
              Member Progress Overview
            </CardTitle>
            <CardDescription>
              Track individual member engagement and spiritual growth
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Member</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Stage</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Journals</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Check-Ins</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Last Activity</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allProgress.map((progress) => (
                    <tr
                      key={progress.user.id}
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`member-row-${progress.user.id}`}
                    >
                      <td className="py-4 px-4">
                        <div>
                          <p className="font-medium text-slate-800">{progress.user.name}</p>
                          <p className="text-xs text-slate-500">{progress.user.email}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <Badge className={`${getStageColor(progress.user.current_stage)} text-xs capitalize`}>
                          {getStageLabel(progress.user.current_stage).split(' - ')[0]}
                        </Badge>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="font-semibold text-slate-700">{progress.journal_count}</span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="font-semibold text-slate-700">{progress.checkin_count}</span>
                      </td>
                      <td className="py-4 px-4 text-sm text-slate-600">
                        {progress.latest_journal || progress.latest_checkin ? (
                          <div>
                            {progress.latest_journal && (
                              <div>Journal: {formatDate(progress.latest_journal)}</div>
                            )}
                            {progress.latest_checkin && (
                              <div>Check-in: {formatDate(progress.latest_checkin)}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">No activity</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="inline-flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setDmRecipient({ id: progress.user.id, name: progress.user.name });
                              setDmOpen(true);
                            }}
                            className="border-[#2B6B7F] text-[#2B6B7F] hover:bg-[#2B6B7F] hover:text-white"
                            data-testid={`message-user-${progress.user.id}`}
                          >
                            <MessageSquarePlus className="w-4 h-4 mr-1" />
                            Message
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewDetails(progress)}
                            className="border-[#2B6B7F] text-[#2B6B7F] hover:bg-[#2B6B7F] hover:text-white"
                            data-testid={`view-details-${progress.user.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Details
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Charts + engagement metrics */}
        <AdminAnalytics />

        {/* What members are searching for */}
        <TopSearchesPanel />

          </TabsContent>

          <TabsContent value="management" className="space-y-6" data-testid="management-tab-content">

        {/* Administrators panel — visible to all admins; grant/revoke controls only for super admin */}
        <AdminAdministratorsPanel />

        {/* Prayer Moderators panel */}
        <AdminPrayerModerators />

        {/* Confidential Requests (admins + prayer moderators) */}
        <ConfidentialPrayersPanel />

        {/* Password Reset Tool */}
        <Card className="mt-8" data-testid="password-reset-tool">
          <CardHeader>
            <CardTitle className="text-2xl font-heading text-[#2B6B7F] flex items-center gap-2">
              <KeyRound className="w-6 h-6" />
              Password Reset Tool
            </CardTitle>
            <CardDescription>
              Generate a one-time password reset link for a member who is locked out. 
              They will be required to set up security questions after resetting.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex gap-3">
                <Input
                  type="email"
                  placeholder="Enter member's email address"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="flex-1"
                  data-testid="reset-email-input"
                />
                <Button
                  onClick={handleGenerateResetLink}
                  disabled={generatingReset || !resetEmail}
                  className="bg-[#2B6B7F] hover:bg-[#1E4D5F]"
                  data-testid="generate-reset-btn"
                >
                  {generatingReset ? 'Generating...' : 'Generate Reset Link'}
                </Button>
              </div>
              
              {resetLinkData && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
                  <div className="flex items-center gap-2 text-green-700 font-medium">
                    <CheckCircle2 className="w-5 h-5" />
                    {resetLinkData.emailSent
                      ? `Reset link emailed to ${resetLinkData.userName}`
                      : `Reset link generated for ${resetLinkData.userName}`}
                  </div>
                  {resetLinkData.emailSent && (
                    <p className="text-sm text-green-700">
                      📧 An email with the reset link has been sent to <strong>{resetLinkData.userEmail}</strong>.
                      They can also use the link below if they don&apos;t receive the email.
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <Input
                      value={resetLinkData.link}
                      readOnly
                      className="flex-1 bg-white text-sm"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(resetLinkData.link)}
                      className="flex-shrink-0"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </Button>
                  </div>
                  {!resetLinkData.emailSent && (
                    <>
                      {resetLinkData.emailError && (
                        <div className="p-2.5 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900">
                          <strong>Email not sent:</strong> {resetLinkData.emailError}
                        </div>
                      )}
                      <p className="text-sm text-green-600">
                        Send this link to {resetLinkData.userEmail}. It will expire in 24 hours.
                      </p>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setResetLinkData(null); setResetEmail(''); }}
                    className="text-slate-500"
                  >
                    Clear
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Prayer Approval Queue */}
        <AdminPendingPrayers />

        {/* Sermon Management Tool */}
        <Card className="mt-8" data-testid="sermon-management-tool">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl font-heading text-[#2B6B7F] flex items-center gap-2">
                  <BookOpen className="w-6 h-6" />
                  Manage Sermons
                </CardTitle>
                <CardDescription>
                  Add new sermons or sync existing ones. AI will auto-generate study questions.
                </CardDescription>
              </div>
              <Badge className="bg-[#2B6B7F]">{allSermons.length} sermons</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Add New Sermon Form */}
              {showSermonForm ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
                  <h4 className="font-semibold text-green-800">Add New Sermon</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm text-slate-600 block mb-1">Week Start Date (Sunday)</label>
                      <Input
                        type="date"
                        value={newSermonDate}
                        onChange={(e) => setNewSermonDate(e.target.value)}
                        className="bg-white"
                        data-testid="new-sermon-date"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-slate-600 block mb-1">Bible Passage</label>
                      <Input
                        placeholder="e.g., Matthew 5:1-12"
                        value={newSermonPassage}
                        onChange={(e) => setNewSermonPassage(e.target.value)}
                        className="bg-white"
                        data-testid="new-sermon-passage"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-green-600">
                    Study questions and parent questions will be auto-generated by AI.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleAddSermon}
                      disabled={addingSermon}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="add-sermon-btn"
                    >
                      {addingSermon ? 'Adding & Generating Questions...' : 'Add Sermon'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setShowSermonForm(false); setNewSermonDate(''); setNewSermonPassage(''); }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={() => setShowSermonForm(true)}
                  variant="outline"
                  className="border-green-500 text-green-600 hover:bg-green-50"
                  data-testid="show-add-sermon-form"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Sermon
                </Button>
              )}

              {/* Bulk Import Panel */}
              <BulkSermonImport onComplete={fetchAllSermons} />

              {/* Sync Button — sized to fit inside the tab even on wide screens */}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={handleSyncSermons}
                  disabled={syncingSermons}
                  className="bg-[#2B6B7F] hover:bg-[#1E4D5F]"
                  data-testid="sync-sermons-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-1.5 ${syncingSermons ? 'animate-spin' : ''}`} />
                  {syncingSermons ? 'Syncing…' : 'Sync Sermons'}
                </Button>
                <Button
                  size="sm"
                  onClick={fetchAllSermons}
                  variant="outline"
                  disabled={loadingSermons}
                >
                  <RefreshCw className={`w-4 h-4 mr-1.5 ${loadingSermons ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>

              {/* Sermon List */}
              <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                {loadingSermons ? (
                  <div className="p-4 text-center text-slate-500">Loading sermons...</div>
                ) : allSermons.length === 0 ? (
                  <div className="p-4 text-center text-slate-500">No sermons found</div>
                ) : (
                  [...allSermons].reverse().map((sermon) => (
                    <div key={sermon.id} className="p-3 hover:bg-slate-50">
                      <div className="flex items-center justify-between">
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => setExpandedSermon(expandedSermon === sermon.id ? null : sermon.id)}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-[#2B6B7F]">{sermon.week_start_date}</span>
                            <span className="text-slate-600">{sermon.passage}</span>
                            {expandedSermon === sermon.id ? (
                              <ChevronUp className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            {sermon.study_questions?.length || 0} study questions, {sermon.parent_questions?.length || 0} parent questions
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteSermon(sermon.id, sermon.passage)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      {expandedSermon === sermon.id && (
                        <div className="mt-3 pl-4 border-l-2 border-slate-200 space-y-2">
                          {sermon.study_questions?.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-slate-600">Study Questions:</p>
                              <ul className="text-xs text-slate-500 list-disc list-inside">
                                {sermon.study_questions.map((q, i) => (
                                  <li key={i}>{q}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {sermon.parent_questions?.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-slate-600">Parent Questions:</p>
                              <ul className="text-xs text-slate-500 list-disc list-inside">
                                {sermon.parent_questions.map((q, i) => (
                                  <li key={i}>{q}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Private Messages Section */}
        <Card className="mt-8" data-testid="private-messages">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl font-heading text-[#2B6B7F] flex items-center gap-2">
                  <Mail className="w-6 h-6" />
                  Private Messages
                </CardTitle>
                <CardDescription>
                  Direct messages from members - only you can see these
                </CardDescription>
              </div>
              {privateMessages.filter(m => !m.read).length > 0 && (
                <Badge className="bg-red-500 text-white">
                  {privateMessages.filter(m => !m.read).length} New
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loadingMessages ? (
              <div className="text-center py-8 text-slate-500">Loading messages...</div>
            ) : privateMessages.length === 0 ? (
              <div className="text-center py-8">
                <Mail className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No private messages yet</p>
                <p className="text-sm text-slate-400">Messages from members will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {privateMessages.map((msg) => (
                  <Card 
                    key={msg.id} 
                    className={`border-l-4 ${!msg.read ? 'border-l-red-500 bg-red-50/30' : msg.admin_reply ? 'border-l-green-500' : 'border-l-amber-500'}`}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          {/* Sender Info */}
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-semibold text-slate-800">{msg.sender_name}</span>
                            <span className="text-xs text-slate-500">({msg.sender_email})</span>
                            {!msg.read && (
                              <Badge className="bg-red-500 text-white text-xs">New</Badge>
                            )}
                            {msg.admin_reply && (
                              <Badge className="bg-green-500 text-white text-xs">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Replied
                              </Badge>
                            )}
                          </div>
                          
                          {/* Subject */}
                          {msg.subject && (
                            <p className="text-sm font-medium text-[#2B6B7F] mb-1">
                              Subject: {msg.subject}
                            </p>
                          )}
                          
                          {/* Message */}
                          <p className="text-slate-700 whitespace-pre-wrap mb-2">{msg.message}</p>
                          
                          <p className="text-xs text-slate-400">
                            Received: {formatDate(msg.created_at)}
                          </p>
                          
                          {/* Admin Reply */}
                          {msg.admin_reply && (
                            <div className="mt-3 p-3 bg-[#2B6B7F]/5 rounded-lg border border-[#2B6B7F]/20">
                              <div className="flex items-center gap-2 mb-1">
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                                <span className="text-sm font-medium text-[#2B6B7F]">Your Reply:</span>
                                <span className="text-xs text-slate-500">{formatDate(msg.replied_at)}</span>
                              </div>
                              <p className="text-slate-700 text-sm whitespace-pre-wrap">{msg.admin_reply}</p>
                            </div>
                          )}
                          
                          {/* Reply Form */}
                          {replyingTo === msg.id && (
                            <div className="mt-3 p-3 bg-slate-50 rounded-lg border">
                              <Textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Type your reply..."
                                rows={3}
                                className="w-full mb-2 resize-none"
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleReply(msg.id)}
                                  disabled={sendingReply || !replyText.trim()}
                                  className="bg-[#2B6B7F] hover:bg-[#1E4D5F]"
                                >
                                  {sendingReply ? 'Sending...' : (
                                    <>
                                      <Send className="w-3 h-3 mr-1" />
                                      Send Reply
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => { setReplyingTo(null); setReplyText(''); }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex flex-col gap-2">
                          {!msg.admin_reply && replyingTo !== msg.id && (
                            <Button
                              size="sm"
                              onClick={() => { 
                                setReplyingTo(msg.id); 
                                setReplyText('');
                                if (!msg.read) handleMarkRead(msg.id);
                              }}
                              className="bg-[#2B6B7F] hover:bg-[#1E4D5F]"
                            >
                              <Send className="w-3 h-3 mr-1" />
                              Reply
                            </Button>
                          )}
                          {msg.admin_reply && replyingTo !== msg.id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setReplyingTo(msg.id); setReplyText(msg.admin_reply); }}
                              className="border-[#2B6B7F] text-[#2B6B7F]"
                            >
                              Edit Reply
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteMessage(msg.id)}
                            className="border-red-500 text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* User Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading text-[#2B6B7F]">
              {selectedUser?.name}&apos;s Progress
            </DialogTitle>
            <DialogDescription>
              Detailed view of journal entries and weekly check-ins
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="journals" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="journals">Journal Entries ({userJournals.length})</TabsTrigger>
              <TabsTrigger value="checkins">Check-Ins ({userCheckins.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="journals" className="space-y-3 mt-4">
              <div className="rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-900 p-3 flex items-start gap-2">
                <Eye className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-blue-600" />
                <p>
                  <strong>Journal contents are private.</strong> Admins can see whether a member is
                  journaling and how often, but never the words they wrote.
                </p>
              </div>
              {userJournals.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No journal entries yet</p>
              ) : (
                userJournals.map((entry) => (
                  <Card key={entry.id} className="border-l-4" style={{ borderLeftColor: '#2B6B7F' }}>
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-center">
                        <Badge className="bg-[#2B6B7F]">
                          {getVitalSignLabel(entry.vital_sign)}
                        </Badge>
                        <span className="text-sm text-slate-500">{formatDate(entry.date)}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="checkins" className="space-y-3 mt-4">
              {userCheckins.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No check-ins yet</p>
              ) : (
                userCheckins.map((checkin) => (
                  <Card key={checkin.id}>
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start mb-4">
                        <h4 className="font-semibold text-slate-800">Week of {formatDate(checkin.week_start_date)}</h4>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <span className="text-sm text-slate-600">Pursue God:</span>
                          <span className="font-bold text-[#2B6B7F]">{checkin.pursue_god_rating}/10</span>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <span className="text-sm text-slate-600">Engage Scripture:</span>
                          <span className="font-bold text-[#5FA8C1]">{checkin.engage_scripture_rating}/10</span>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <span className="text-sm text-slate-600">Serve Others:</span>
                          <span className="font-bold text-[#7BA888]">{checkin.serve_others_rating}/10</span>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <span className="text-sm text-slate-600">Radiate Jesus:</span>
                          <span className="font-bold text-[#D4A574]">{checkin.radiate_jesus_rating}/10</span>
                        </div>
                      </div>
                      {checkin.notes && (
                        <div className="pt-3 border-t border-slate-200">
                          <p className="text-sm text-slate-600 mb-1 font-medium">Notes:</p>
                          <p className="text-sm text-slate-700">{checkin.notes}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <SendDirectMessageDialog
        open={dmOpen}
        onOpenChange={setDmOpen}
        recipient={dmRecipient}
      />
    </div>
  );
};

export default AdminDashboard;
